import { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format, parseISO, subDays, subYears } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DateRangePicker from "@/components/ui/date-range-picker";
import { CustomerSearch } from "@/features/customers/customer-search";
import { useCumulativeOrderAnalytics } from "@/api/analytics";

type PresetKey = "7d" | "30d" | "90d" | "1y";

const presets: { key: PresetKey; label: string }[] = [
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
  { key: "1y", label: "1Y" },
];

function getPresetRange(key: PresetKey): DateRange {
  const to = new Date();
  switch (key) {
    case "7d":
      return { from: subDays(to, 7), to };
    case "30d":
      return { from: subDays(to, 30), to };
    case "90d":
      return { from: subDays(to, 90), to };
    case "1y":
      return { from: subYears(to, 1), to };
  }
}

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export default function CumulativeOrdersChart() {
  const [activePreset, setActivePreset] = useState<PresetKey | null>("30d");
  const [dateRange, setDateRange] = useState<DateRange>(getPresetRange("30d"));
  const [customerId, setCustomerId] = useState<string>("");

  const { data: response, isLoading } = useCumulativeOrderAnalytics(
    dateRange,
    customerId || undefined
  );

  const chartData = useMemo(() => {
    if (!response?.data?.length) return [];
    return response.data.map((item) => ({
      ...item,
      formattedDate: format(parseISO(item.date), "MMM d"),
    }));
  }, [response]);

  const handlePreset = (key: PresetKey) => {
    setActivePreset(key);
    setDateRange(getPresetRange(key));
  };

  const handleDateChange = (range: DateRange) => {
    setActivePreset(null);
    setDateRange(range);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cumulative Orders</CardTitle>
        <div className="flex flex-col sm:flex-row gap-3 mt-2">
          <div className="flex gap-1">
            {presets.map((p) => (
              <Button
                key={p.key}
                variant={activePreset === p.key ? "default" : "outline"}
                size="sm"
                onClick={() => handlePreset(p.key)}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <DateRangePicker date={dateRange} onDateChange={handleDateChange} />
          <div className="w-[250px]">
            <CustomerSearch
              value={customerId || null}
              onValueChange={setCustomerId}
              placeholder="All customers"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[350px] flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[350px] flex items-center justify-center text-muted-foreground">
            No data for selected range
          </div>
        ) : (
          <div className="w-full h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formattedDate" />
                <YAxis
                  yAxisId="revenue"
                  orientation="left"
                  tickFormatter={(v: number) => formatCurrency(v)}
                />
                <YAxis yAxisId="count" orientation="right" />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === "Revenue") return formatCurrency(value);
                    return value;
                  }}
                  labelFormatter={(label: string) => label}
                />
                <Legend />
                <Line
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="cumulativeRevenueCents"
                  stroke="#4f46e5"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name="Revenue"
                />
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="cumulativeCount"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name="Orders"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
